import { useBreakpointValue } from "@gluestack-style/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigation } from "@repo/core/hooks/useNavigation";
import { ROUTES } from "@repo/core/routes";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AuthorizerResource } from "@repo/core/services/resources/authorizer";
import { AuthorizerConsultPlanTermsResponseDTO } from "@repo/core/services/entities/Authorizer";
import { apiInstance } from "@repo/core/services/instances/api";
import {
  InstallmentSimulateOperationTypeEnum,
  InterestTypeEnum
} from "@repo/core/enums/installments";
import { useRouteParams } from "@repo/core/hooks/useRouteParams";
import { CardResource } from "@repo/core/services/resources/card";

const formSchema = z.object({
  purchaseValue: z.string()
});

export type FormDataType = z.infer<typeof formSchema>;
const cardResource = new CardResource(apiInstance);
const authorizerResource = new AuthorizerResource(apiInstance);

export const SimulateInstallmentsController = () => {
  const isMobile = useBreakpointValue({ base: true, md: false });
  const { back, push } = useNavigation();

  const params: {
    dblValorOperacao: string;
    lngCartao: string;
    strProduto: string;
    operationType: InstallmentSimulateOperationTypeEnum;
  } = useRouteParams();

  const { dblValorOperacao, lngCartao, strProduto, operationType } = params;
  const [selectedInterestType, setSelectedInterestType] =
    useState<InterestTypeEnum>(InterestTypeEnum.WITH_INTEREST);
  const [selectedInstallmentId, setSelectedInstallmentId] = useState<
    number | null
  >(null);

  const {
    data: installmentOptions,
    isFetching: isFetchingInstallments,
    isRefetching: isRefetchingInstallments
  } = useQuery<AuthorizerConsultPlanTermsResponseDTO>({
    queryKey: [
      "consultPlanTerms",
      selectedInterestType,
      strProduto,
      lngCartao,
      dblValorOperacao,
      operationType
    ],
    queryFn: async () => {
      const response = await authorizerResource.consultPlanTerms({
        strProduto,
        lngCartao,
        // Mock till Store Select Implementation
        strCodigoEstabelecimento: "000000000000593",
        strTipoOperacao: operationType,
        dblValorOperacao: parseFloat(dblValorOperacao),
        strSemJuros:
          selectedInterestType === InterestTypeEnum.WITHOUT_INTEREST
            ? "S" // Yes
            : "N" // No
      });

      const installmentsOptions = response.data;
      // Reverse the installment options for the "Sem Juros" option, as it's not in descending order
      if (installmentsOptions[1] && installmentsOptions[1].lstPrazos) {
        installmentsOptions[1].lstPrazos =
          installmentsOptions[1].lstPrazos.reverse();
      }

      return installmentsOptions;
    },
    enabled: !!dblValorOperacao && !!lngCartao && !!strProduto
  });

  const {
    control,
    formState: { isValid, isSubmitting },
    handleSubmit
  } = useForm<FormDataType>({
    resolver: zodResolver(formSchema),
    defaultValues: { purchaseValue: dblValorOperacao }
  });

  const handleInterestTypeChange = (type: InterestTypeEnum) => {
    setSelectedInterestType(type);
    setSelectedInstallmentId(null);
  };

  const handleInstallmentChange = (id: number) => {
    setSelectedInstallmentId(id);
  };

  const handleOnSubmitForm = handleSubmit(async () => {
    if (selectedInstallmentId === null) return;
    const selectedInstallmentOption =
      selectedInterestType === InterestTypeEnum.WITH_INTEREST
        ? installmentOptions?.[0]?.lstPrazos?.[selectedInstallmentId]
        : installmentOptions?.[1]?.lstPrazos?.[selectedInstallmentId];

    const profileData = await cardResource.consultData(
      strProduto,
      Number(lngCartao)
    );

    if (!selectedInstallmentOption || !profileData.data) return;

    const params: {
      selectedInstallmentOption: string;
      profileData: string;
    } = {
      selectedInstallmentOption: JSON.stringify(selectedInstallmentOption),
      profileData: JSON.stringify(profileData.data)
    };

    push(ROUTES.simulateInstallmentsReceipt, params);
  });

  return {
    back,
    isMobile,
    control,
    isValid,
    isSubmitting,
    handleOnSubmitForm,
    dblValorOperacao,
    selectedInterestType,
    handleInterestTypeChange,
    installmentOptions:
      selectedInterestType === InterestTypeEnum.WITH_INTEREST
        ? installmentOptions?.[0]?.lstPrazos
        : installmentOptions?.[1]?.lstPrazos,
    selectedInstallmentId,
    handleInstallmentChange,
    isFetching: isFetchingInstallments || isRefetchingInstallments
  };
};
